import join from 'url-join';

import { getENV } from '@libs/common-next';
import { logSentryError } from '@libs/common-next/utils';
import type { StoreService } from '@libs/common-node';
import type { StoreV2 } from '@libs/common-types';
import { isValidUrl } from '@libs/core';

import { SentryErrors } from '@/shared/sentry';

type ServiceUrlFunctions = {
    PetsHotel: (topLevelDomain: string, storeNumber: number) => string;
    Adoptions: () => string;
    Veterinary: (topLevelDomain: string) => string;
};

type ConstructServiceURLOptions = {
    isPetsHotelLocalUrlEnabled?: boolean;
};

export const VETERINARY = 'veterinary';

const SERVICE_URLS: ServiceUrlFunctions = {
    PetsHotel: (topLevelDomain: string, storeNumber: number) => {
        return process.env.NEXT_PUBLIC_APP_ENV?.toLowerCase() === 'prod'
            ? `https://services.petsmart.${topLevelDomain}/petshotel/${storeNumber}`
            : `https://services-staging.petsmart.${topLevelDomain}/petshotel/${storeNumber}`;
    },
    Adoptions: () => 'https://petsmartcharities.org/adopt-a-pet',
    Veterinary: (topLevelDomain: string) =>
        `https://www.petsmart.${topLevelDomain}/pet-services/petsmart-veterinary-care.html`,
};

const SERVICE_PATHS: Record<string, string> = {
    Training: 'dog-training',
    Grooming: 'grooming',
    'Doggie Day Camp': 'petshotel-doggie-day-camp',
};

export const servicesToDisplay = ['Veterinary', 'PetsHotel', 'Grooming', 'Doggie Day Camp', 'Training', 'Adoptions'];

const makeSnakeCase = (str: string) =>
    str
        .replace(/^(N\.|S\.|E\.|W\.)\s*/i, '')
        .replace(/[()]/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .toLowerCase();

function getVeterinaryCareUrl(topLevelDomain: string, store: StoreV2, veterinaryCareUrl: string | undefined): string {
    if (store.CountryAbbreviation === 'US' && veterinaryCareUrl) {
        if (isValidUrl(veterinaryCareUrl)) {
            return veterinaryCareUrl;
        }

        logSentryError(veterinaryCareUrl, {
            ...SentryErrors.StoreDetailsServicesModule.InvalidVeterinaryCareUrl,
            extra: {
                ...SentryErrors.StoreDetailsServicesModule.InvalidVeterinaryCareUrl.extra,
                storeNumber: String(store.StoreNumber),
                invalidUrl: veterinaryCareUrl,
            },
        });
    }

    return SERVICE_URLS.Veterinary(topLevelDomain);
}

export function constructServiceURL(
    service: StoreService,
    store: StoreV2,
    { isPetsHotelLocalUrlEnabled = false }: ConstructServiceURLOptions = {}
): string | undefined {
    const isCA = getENV('NEXT_PUBLIC_LOCALE') === 'en-CA';
    const topLevelDomain = isCA ? 'ca' : 'com';
    const servicePath =
        isPetsHotelLocalUrlEnabled && service.ServiceName === 'PetsHotel'
            ? SERVICE_PATHS['Doggie Day Camp']
            : SERVICE_PATHS[service.ServiceName];

    if (servicePath) {
        return join(
            `https://stores.petsmart.${topLevelDomain}`,
            store.StateProvinceAbbreviation.toLowerCase(),
            makeSnakeCase(store.City),
            makeSnakeCase(store.Name),
            servicePath
        );
    }

    if (service.ServiceName.toLowerCase() === VETERINARY) {
        return getVeterinaryCareUrl(topLevelDomain, store, service.ServicePartnerLink);
    }

    return SERVICE_URLS[service.ServiceName as keyof ServiceUrlFunctions]?.(topLevelDomain, store.StoreNumber);
}
const scene7StaticBase = getENV('NEXT_PUBLIC_SCENE7_STATIC_BASE');

export const serviceImages: Record<string, string> = {
    Grooming: `${scene7StaticBase}WEB-24-2287450-May24-StoreLoc-Grooming?fmt=webp&wid=60&hei=60`,
    Adoptions: `${scene7StaticBase}WEB-24-2287450-May24-StoreLoc-Adopt?fmt=webp&wid=60&hei=60`,
    Training: `${scene7StaticBase}WEB-24-2287450-May24-StoreLoc-Training?fmt=webp&wid=60&hei=60`,
    'PetSmart Veterinary Services': `${scene7StaticBase}Pet_Store_Locator_VIU_16TU_DT?fmt=webp&wid=60&hei=60`,
    Veterinary: `${scene7StaticBase}Pet_Store_Locator_VIU_16TU_DT?fmt=webp&wid=60&hei=60`,
    'Doggie Day Camp': `${scene7StaticBase}WEB-24-2287450-May24-StoreLoc-DDC?fmt=webp&wid=60&hei=60`,
    PetsHotel: `${scene7StaticBase}WEB-24-2287450-May24-StoreLoc-PetsHotel?fmt=webp&wid=60&hei=60`,
};
